/**
 * `@makan/contracts/client`: the part of the kit a browser bundle may
 * import. It imports nothing (rule R20), so a feature's values entry builds
 * on it at no cost to the public page.
 *
 * Routes are data: a feature declares its pages here, in its values entry
 * (features/<f>/domain/values.ts), so the command center, the feature map,
 * page recipes and other features read one table, and node tools read it
 * without loading a page.
 */
export type RouteAccess = 'public' | 'staff';

export interface RouteDecl {
  readonly path: `/${string}`;
  readonly access: RouteAccess;
  /** One line: what the page is for. */
  readonly intent: string;
  /** Where `/` and sign-in send a signed-in staff member. Exactly one route across all features. */
  readonly staffHome?: true;
  /** Where a group's own host opens on its bare address (`https://member.example.com/`): a public route of `:groupId`. At most one across all features. */
  readonly hostHome?: true;
}

export type RouteTable = Readonly<Record<string, RouteDecl>>;

/**
 * A staff route takes its group from the signed-in staff member's session,
 * never from its path: it names no `:groupId` and does not start with `/g/`,
 * the prefix of the old staff links the shell redirects.
 */
type StaffPathsChecked<R extends RouteTable> = {
  readonly [K in keyof R]: R[K]['access'] extends 'staff' ? (R[K]['path'] extends `${string}:groupId${string}` | `/g/${string}` ? never : R[K]) : R[K];
};

export function defineRoutes<const R extends RouteTable>(routes: R & StaffPathsChecked<R>): R {
  return routes;
}

/** `/door/:groupId/things/:thingId` -> `{ readonly groupId: string; readonly thingId: string }`: the params a route's path names. */
export type ParamsOf<P extends string> = P extends `${string}:${infer Name}/${infer Rest}`
  ? { readonly [K in Name]: string } & ParamsOf<`/${Rest}`>
  : P extends `${string}:${infer Name}`
    ? { readonly [K in Name]: string }
    : unknown;

/** The URL of a declared route: its path with the params filled in, and the query. Code that links to a page writes this, never the path. */
export function href<D extends RouteDecl>(route: D, params: ParamsOf<D['path']>, query: Readonly<Record<string, string>> = {}): string {
  const values = params as Readonly<Record<string, string>>;
  const path = route.path.replace(/:([A-Za-z]\w*)/g, (_match, name: string) => {
    const value = values[name];
    if (value === undefined) throw new Error(`${route.path}: no value for :${name}`);
    return encodeURIComponent(value);
  });
  const search = Object.entries(query)
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
    .join('&');
  return search === '' ? path : `${path}?${search}`;
}

/**
 * The path of a public route on its group's own host, where the host names
 * the group: href without the `/:groupId` segment (`/join/<id>` is `/join`).
 */
export function hrefOnHost<D extends RouteDecl>(route: D, params: ParamsOf<D['path']>, query: Readonly<Record<string, string>> = {}): string {
  return href({ ...route, path: route.path.replace(/\/:groupId(?=\/|$)/, '') || '/' }, params, query);
}

/** Days in each month of a leap year: a birthday has no year, so 29 February is a day (DayMonth, and the page's day picker). */
export const DAYS_IN_MONTH = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31] as const;

/** The markets a group may trade in: zod-free, so a page and a values module read the same table the decider does. */
export * from './market.ts';

/**
 * The languages pages are written in: English, Simplified Chinese and Bahasa
 * Melayu. The command center's kit picks a page's words by them, and a
 * command that records the language a person used (the member's, for the
 * messages they get) takes this union, so a page and a decider cannot
 * disagree on which exist. Adding one is a row here and its words in every
 * strings.ts, which the compiler then asks for.
 */
export const LANGUAGES = ['en', 'zh', 'ms'] as const;
export type Language = (typeof LANGUAGES)[number];
